import { useEffect } from "react";

import {
  Column,
  ErrorText,
  LoadingIndicator,
  Row,
  TopbarContainer,
  TopbarTitle,
} from "@/components";
import { SadieVoiceSimulatorCall } from "@/components/tools/sadie-voice-simulator/SadieVoiceSimulatorCall";
import { SadieVoicePhoneCallButton } from "@/components/tools/sadie-voice-simulator/SadieVoicePhoneCallButton";
import { SadieVoicePostCallPanel } from "@/components/tools/sadie-voice-simulator/SadieVoicePostCallPanel";
import { SadieVoiceSimulatorControls } from "@/components/tools/sadie-voice-simulator/SadieVoiceSimulatorControls";
import { Button } from "@ui/button";

import { useRunSadieVoicePostCall } from "@/hooks/mutations/useRunSadieVoicePostCall";
import { useSadieVoiceCall } from "@/hooks/useSadieVoiceCall";
import { useSadieVoiceSimulator } from "@/hooks/useSadieVoiceSimulator";
import { useSetTitle } from "@/hooks/useSetTitle";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { sadieVoiceSimulator } from "@/lib/sadieVoiceSimulator";

export const ToolsSadieVoiceSimulatorPage = () => {
  /* React Hooks */
  const simulator = useSadieVoiceSimulator();
  const call = useSadieVoiceCall();
  const postCall = useRunSadieVoicePostCall();

  useSetTitle({ title: "Tools | Sadie Voice Simulator" });

  useEffect(() => {
    mixpanelUtils.trackScreenView(MIXPANEL_SCREENS.SADIE_VOICE_SIMULATOR);
  }, []);

  /* Render */
  const render = () => {
    if (simulator.defaultsStatus === "pending") {
      return <LoadingIndicator variant="medium" />;
    }
    if (simulator.defaultsStatus === "error" || !simulator.defaults) {
      return (
        <Row className="items-center justify-center gap-2 py-2">
          <ErrorText errorMessage="Could not load the voice simulator defaults." />
          <Button
            onClick={() => void simulator.refetchDefaults()}
            size="sm"
            variant="outline"
          >
            Retry
          </Button>
        </Row>
      );
    }
    const inCall =
      call.status === "connecting" ||
      call.status === "active" ||
      call.status === "ending";
    return (
      // Each pane scrolls on its own, as on the text simulator.
      <Row className="min-h-0 flex-1 overflow-hidden">
        <Column className="min-h-0 w-[400px] shrink-0 overflow-y-auto border-r border-slate-200 bg-slate-50 p-4">
          <SadieVoiceSimulatorControls
            disabled={inCall}
            simulator={simulator}
          />
        </Column>
        <SadieVoiceSimulatorCall
          // Live needs the caller's prompts filled first.
          canStart={
            !inCall && (simulator.engine !== "live" || !!simulator.bundle)
          }
          captions={call.captions}
          error={call.error}
          gaps={call.gaps}
          isMuted={call.isMuted}
          onHangUp={call.hangUp}
          onStart={() => {
            postCall.reset();
            void call.start(simulator.callOptions());
          }}
          onToggleMute={call.toggleMute}
          liveActions={
            call.session?.engine === "live" && simulator.bundle
              ? [simulator.bundle.troubleNote, simulator.bundle.guard].map(
                  (note) => ({
                    label: `${note.key}: ${note.label}`,
                    onClick: () => {
                      call.pushNote(
                        note.key === "G01" ? "instructions" : "thinking",
                        note.content,
                        `Pushed ${note.key}: ${note.label}`,
                      );
                    },
                  }),
                )
              : []
          }
          phoneCallButton={
            <SadieVoicePhoneCallButton
              disabled={simulator.engine !== "live" || !simulator.bundle}
              inboundCallSetup={simulator.inboundCallSetup}
            />
          }
          postCall={
            call.session?.engine === "live" ? (
              <SadieVoicePostCallPanel
                isError={postCall.isError}
                isPending={postCall.isPending}
                onRun={() => {
                  postCall.mutate({
                    prompt: simulator.liveField("postCallPrompt"),
                    transcript: sadieVoiceSimulator.transcriptOf(call.captions),
                  });
                }}
                result={postCall.data}
              />
            ) : null
          }
          sessionId={call.session?.sessionId ?? null}
          startedAt={call.startedAt}
          status={call.status}
          warning={call.warning}
        />
      </Row>
    );
  };

  return (
    <Column className="h-svh">
      <TopbarContainer>
        <TopbarTitle>Sadie Voice Simulator</TopbarTitle>
      </TopbarContainer>
      {render()}
    </Column>
  );
};
